import { Alert, Button, PinInput, toast } from '@goodneighbor/ui';
import styles from './otpForm.module.scss';
import { Fragment, useCallback, useEffect, useState } from 'react';

const RESEND_COOLDOWN_SECONDS = 60;

export default function OtpForm({
  onSubmit,
  email,
  otpValue,
  handleOtpChange,
  otpLength,
  verificationError,
  isVerifyOtpPending,
  handleResend,
  isSendOtpPending
}: {
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  email: string;
  otpValue: string;
  handleOtpChange: (value: string) => void;
  otpLength: number;
  verificationError?: string | null;
  isVerifyOtpPending: boolean;
  handleResend: () => Promise<boolean>;
  isSendOtpPending: boolean;
}) {
  const [timeLeft, setTimeLeft] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleResendWithReset = useCallback(async () => {
    const success = await handleResend();
    if (success) {
      setTimeLeft(RESEND_COOLDOWN_SECONDS);
      toast({
        title: 'Code sent',
        description: 'A new verification code has been sent to your email.',
        colorScheme: 'success'
      });
    }
  }, [handleResend]);

  return (
    <form className={styles.verificationContainer} onSubmit={onSubmit}>
      <div className={styles.verifyHeader}>
        <h2 className={styles.verificationTitle}>Confirm your email</h2>
        <p className={styles.verificationSubtitle}>
          We sent a verification code to{' '}
          <span className={styles.emailHighlight}>{email}</span>
        </p>
      </div>

      <PinInput className={styles.pinInputWrapper}>
        <PinInput.Group
          value={otpValue}
          onChange={handleOtpChange}
          maxLength={otpLength}
          containerClassName={`${styles.otpInputRow} ${
            verificationError ? styles.otpInputError : ''
          }`}
        >
          {Array.from({ length: otpLength }).map((_, index) => (
            <Fragment key={index}>
              <PinInput.Slot
                index={index}
                className={`${styles.otpSlot} ${
                  otpValue[index] ? styles.otpSlotFilled : styles.otpSlotEmpty
                }`}
              />
              {index === 2 && (
                <PinInput.Separator
                  className={styles.otpSeparator}
                  aria-hidden
                />
              )}
            </Fragment>
          ))}
        </PinInput.Group>
      </PinInput>

      {verificationError && (
        <Alert
          title="Something went wrong"
          description={verificationError}
          color="error"
          className={styles.alert}
        />
      )}

      <Button
        type="submit"
        color="primary"
        size="lg"
        className={styles.submitButton}
        isLoading={isVerifyOtpPending}
        isDisabled={isVerifyOtpPending}
      >
        Complete Verification
      </Button>

      <div className={styles.verificationActions}>
        <Button
          type="button"
          color="link-gray"
          onClick={handleResendWithReset}
          isDisabled={isSendOtpPending || timeLeft > 0}
          disabled={isSendOtpPending || timeLeft > 0}
        >
          {timeLeft > 0 ? `Resend code in ${timeLeft}s` : 'Resend code'}
        </Button>
      </div>
    </form>
  );
}
